import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActivityStatusOptions, PracticeType } from './calendarModels';
import CalendarRow from './calendarRow';
import {
  HttpError,
  PraksiskalenderAktivitetDto,
  PraksisklasseDto,
} from '../api/generated/studiested-generated-api';
import { StudiestedApiContext } from '../api/StudiestedApiContext';
import { ConfirmationDialog } from '../components/dialog';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import { CalendarUhaContext } from './calendarUhaContext';
import {
  PracticeGroupManipulation,
  UHAKlasserMedEmne,
} from '../components/calendar';

const groupKlasserOnBokstav = (klasser: PraksisklasseDto[]) =>
  klasser.reduce(
    (
      acc: { [bokstav: string]: PraksisklasseDto[] },
      klasse: PraksisklasseDto,
    ) => {
      const { bokstav } = klasse;

      return {
        ...acc,
        [bokstav]: bokstav in acc ? acc[bokstav].concat(klasse) : [klasse],
      };
    },
    {},
  );

interface Props {
  practiceType: PracticeType;
  practicePeriodsFiltered: {
    [klasseId: string]: PraksiskalenderAktivitetDto[];
  };
  shouldRenderHeader: boolean;
  onActivityStatusFilterChanged: Function;
  checkIfGroupCanBeSafelyDeleted: (klasseId: string) => boolean;
}

const PracticeTypeGroups = React.memo<Props>(
  ({
    practiceType,
    practicePeriodsFiltered,
    shouldRenderHeader,
    onActivityStatusFilterChanged,
    checkIfGroupCanBeSafelyDeleted,
  }) => {
    const { formatMessage } = useIntl();

    const { praksiskalenderApi } = useContext(StudiestedApiContext);
    const { addNotification } = useContext(NotificationFunctionContext);
    const { manipulateGroup } = useContext(CalendarUhaContext);

    const [groupsPendingDelete, setGroupsPendingDelete] = useState<
      PraksisklasseDto[]
    >([]);

    const labels = useMemo(
      () => ({
        groupAdded: formatMessage({
          id: 'calendaruha.practiceTypeGroups.groupAdded',
          defaultMessage: 'Group successfully added',
        }),
        groupDeleted: formatMessage({
          id: 'calendaruha.practiceTypeGroups.groupRemoved',
          defaultMessage: 'Group successfully removed',
        }),
        deleteGroup: formatMessage({
          id: 'calendaruha.practiceTypeGroups.confirmationDialog.title',
          defaultMessage: '[!]Delete group',
        }),
        confirmationContent1: formatMessage({
          id: 'calendaruha.practiceTypeGroups.confirmationDialog.contents1',
        }),
        confirmationContent2: formatMessage({
          id: 'calendaruha.practiceTypeGroups.confirmationDialog.contents2',
        }),
        confirmationContent3: formatMessage({
          id: 'calendaruha.practiceTypeGroups.confirmationDialog.contents3',
        }),
      }),
      [formatMessage],
    );

    const successNotification = useCallback(
      (msg: string) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: msg,
        }),
      [addNotification],
    );

    const errorNotification = useCallback(
      (error: HttpError) =>
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: error.body,
        }),
      [addNotification],
    );

    const addGroupHandler = useCallback(() => {
      const { klasser, emneCode, emneName } = practiceType;

      Promise.all(
        klasser
          .filter((klasse) => klasse.bokstav === 'A')
          .map((klasse) =>
            praksiskalenderApi.praksisklassePost({
              nyPraksisklasseDto: { id: klasse.id },
            }),
          ),
      )
        .then((praksisklasseDtos) => {
          successNotification(labels.groupAdded);
          manipulateGroup(
            {
              emne: { kode: emneCode, navn: emneName },
              klasser: praksisklasseDtos,
            },
            PracticeGroupManipulation.ADD,
          );

          onActivityStatusFilterChanged(ActivityStatusOptions.UNFILTERED);
        })
        .catch(errorNotification);
    }, [
      labels.groupAdded,
      practiceType,
      praksiskalenderApi,
      successNotification,
      errorNotification,
      manipulateGroup,
      onActivityStatusFilterChanged,
    ]);

    const deleteGroup = useCallback(
      (klasse: PraksisklasseDto) =>
        praksiskalenderApi
          .praksisklasseIdDelete({ pathParams: { id: klasse.id } })
          .then(() => klasse.bokstav),
      [praksiskalenderApi],
    );

    const onSuccessfulDelete = useCallback(
      (klasserMedEmne: UHAKlasserMedEmne) => {
        successNotification(labels.groupDeleted);
        manipulateGroup(klasserMedEmne, PracticeGroupManipulation.DELETE);
      },
      [successNotification, labels.groupDeleted, manipulateGroup],
    );

    const deleteGroupHandler = useCallback(
      (klasser: PraksisklasseDto[]) => {
        const { emneCode, emneName } = practiceType;
        const groupCanBeSafelyDeleted = klasser.every((klasse) =>
          checkIfGroupCanBeSafelyDeleted(klasse.id),
        );

        if (groupCanBeSafelyDeleted) {
          Promise.all(klasser.map(deleteGroup))
            .then(() =>
              onSuccessfulDelete({
                emne: { kode: emneCode, navn: emneName },
                klasser,
              }),
            )
            .catch(errorNotification);
        } else {
          setGroupsPendingDelete(klasser);
        }
      },
      [
        checkIfGroupCanBeSafelyDeleted,
        deleteGroup,
        errorNotification,
        practiceType,
        onSuccessfulDelete,
      ],
    );

    const groupsPendingDeleteModalClose = (confirmed: boolean) => {
      const { emneCode, emneName } = practiceType;
      if (confirmed) {
        Promise.all(groupsPendingDelete.map(deleteGroup))
          .then(() =>
            onSuccessfulDelete({
              emne: { kode: emneCode, navn: emneName },
              klasser: groupsPendingDelete,
            }),
          )
          .catch(errorNotification);
      }

      setGroupsPendingDelete([]);
    };

    const CalendarRows = useMemo<React.FC>(
      () => () => (
        <>
          {Object.entries(groupKlasserOnBokstav(practiceType.klasser)).map(
            ([bokstav, klasser], i: number) => {
              const aktiviteter = klasser
                .flatMap((klasse) => practicePeriodsFiltered[klasse.id])
                .filter((value) => value !== undefined);
              return (
                <CalendarRow
                  key={i}
                  hasGroups={practiceType.klasser.length > 1}
                  isFirstGroup={i === 0}
                  practiceType={practiceType}
                  klasser={klasser}
                  aktiviteter={aktiviteter}
                  shouldRenderHeader={shouldRenderHeader && i === 0}
                  onAddGroup={addGroupHandler}
                  onDeleteGroup={deleteGroupHandler}
                />
              );
            },
          )}
        </>
      ),
      [
        addGroupHandler,
        deleteGroupHandler,
        practicePeriodsFiltered,
        practiceType,
        shouldRenderHeader,
      ],
    );

    return (
      <>
        <CalendarRows />
        <ConfirmationDialog
          isOpen={groupsPendingDelete.length > 0}
          onClose={groupsPendingDeleteModalClose}
          title={labels.deleteGroup}
          dataCy={'delete-praksisklasse-confirmation-dialog'}
        >
          <p>{labels.confirmationContent1}</p>
          <p>{labels.confirmationContent2}</p>
          <p>{labels.confirmationContent3}</p>
        </ConfirmationDialog>
      </>
    );
  },
);

export default PracticeTypeGroups;
